﻿import { TrackBasket, Tracker, TrackFavorite, TrackItem } from './tk.tracker';

export default abstract class BaseTracker implements Tracker {
    trackingId: string;
    url: string;
    tracker: unknown[];

    endpoint?: string;
    basketEventMap: Record<string, string>;
    favoriteEventMap: Record<string, string>;
    favoriteActionMap: Record<string, string>;

    constructor(
        trackingId: string,
        tracker: unknown[],
        defaultEndpoint: string,
        options?: {
            endpoint?: string;
            basketEventMap?: Record<string, string>;
            favoriteEventMap?: Record<string, string>;
            favoriteActionMap?: Record<string, string>;
        },
    ) {
        this.trackingId = trackingId;
        this.tracker = tracker;
        this.endpoint = options?.endpoint || defaultEndpoint;
        this.url = this.endpoint.replace(/%KEY/g, trackingId);

        this.basketEventMap = options?.basketEventMap || {
            added: 'add_to_cart',
            deleted: 'remove_from_cart',
            changed: 'update_cart',
        };
        this.favoriteEventMap = options?.favoriteEventMap || {
            added: 'add_from_wishlist',
            deleted: 'remove_from_wishlist',
        };
        this.favoriteActionMap = options?.favoriteActionMap || {
            added: 'add',
            deleted: 'delete',
        };
    }

    protected loadScript(): Promise<HTMLScriptElement> {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.async = true;
            script.src = this.url;
            script.onload = () => resolve(script);
            script.onerror = () => reject(new Error(`Failed to load script: ${this.url}`));
            document.head.appendChild(script);
        });
    }

    /**
     * Initializes the tracker by setting up the required configuration and loading the tracking script.
     *
     * @returns {Promise<void>} A promise that resolves when the initialization is complete.
     */
    abstract init(): Promise<void>;

    /**
     * Pushes a generic tracking item to the data layer.
     *
     * @param {TrackItem} item - The tracking item containing the event and associated data.
     */
    abstract push(item: TrackItem): void;

    /**
     * Pushes a basket event to the data layer.
     *
     * @param {TrackBasket} item - The basket tracking item containing event type, currency, value, and item details.
     */
    abstract pushBasket(item: TrackBasket): void;

    /**
     * Pushes a favorite event to the data layer.
     *
     * @param {TrackFavorite} item - The favorite tracking item containing action type, category, name, and value.
     */
    abstract pushFavorite(item: TrackFavorite): void;
}